export interface RingSlot {
  /** Centre of the seat in px from the stage's top-left corner. */
  x: number;
  y: number;
  dir: 'bottom' | 'left' | 'top' | 'right';
}

/**
 * `n` seats spaced at equal straight-line distances along the ellipse that just fits the stage (every seat box stays
 * inside it). Slot 0 is the bottom-centre (the local player); the others run clockwise: left, top, right.
 */
export function ringSlots(n: number, w: number, h: number, seatW: number, seatH: number): RingSlot[] {
  const rx = Math.max(0, w / 2 - seatW / 2 - 2);
  const ry = Math.max(0, h / 2 - seatH / 2);
  const pt = (t: number) => ({ x: w / 2 - rx * Math.sin(t), y: h / 2 + ry * Math.cos(t) });
  const dist = (t1: number, t2: number) => Math.hypot(pt(t1).x - pt(t2).x, pt(t1).y - pt(t2).y);
  // Parameter reached after stepping from `t` to the next point that is exactly `c` away in a straight line.
  const next = (t: number, c: number) => {
    const grid = (2 * Math.PI) / 2000;
    let hi = t;
    while (hi - t < Math.PI && dist(t, hi) < c) hi += grid;
    let lo = hi - grid;
    for (let k = 0; k < 40; k++) {
      const mid = (lo + hi) / 2;
      if (dist(t, mid) < c) lo = mid;
      else hi = mid;
    }
    return (lo + hi) / 2;
  };
  const lap = (c: number) => {
    let t = 0;
    for (let i = 0; i < n; i++) t = next(t, c);
    return t;
  };
  // Bisect the neighbour distance so that n equal steps close the loop exactly.
  let lo = 0;
  let hi = 2 * Math.hypot(rx, ry) + 1;
  for (let k = 0; k < 50; k++) {
    const mid = (lo + hi) / 2;
    if (lap(mid) < 2 * Math.PI) lo = mid;
    else hi = mid;
  }
  const c = (lo + hi) / 2;
  const out: RingSlot[] = [];
  let t = 0;
  for (let i = 0; i < n; i++) {
    const p = pt(t);
    const dir = i === 0 ? 'bottom' : p.x < w * 0.38 ? 'left' : p.x > w * 0.62 ? 'right' : 'top';
    out.push({ x: p.x, y: p.y, dir });
    t = next(t, c);
  }
  return out;
}
